import type { SelectProps } from 'antd';
import { Select } from 'antd';
import getUnicodeFlagIcon from 'country-flag-icons/unicode';
import { GlobalOutlined } from '@ant-design/icons';
import { getCountries, getCountryCallingCode } from 'react-phone-number-input';

export const ALL_COUNTRY_VALUE = 'all';

export const countryList = getCountries();

// No allowedCountries means no filter. A country with nothing to judge against always stays allowed.
// Normalizes the candidate to uppercase: a stored location's country is not normalized anywhere in
// this repo, and allowedCountries is always canonical uppercase ISO codes.
export const isCountryAllowed = (allowedCountries: string[] | undefined, country: string | undefined): boolean =>
  !allowedCountries?.length || !country || allowedCountries.includes(country.toUpperCase());

export interface CountryCodeDropdownProps extends SelectProps<any> {
  allowAll?: boolean;
  customSort?: (a: any, b: any) => number;
  allowedCountries?: string[];
  showCountryCallingCode?: boolean;
}

export const CountryCodeDropdown = ({
  allowAll,
  customSort,
  allowedCountries,
  showCountryCallingCode,
  ...props
}: CountryCodeDropdownProps) => {
  if (customSort) {
    countryList.sort(customSort);
  }

  const options: SelectProps['options'] = [];
  if (allowAll) {
    options.push({
      key: ALL_COUNTRY_VALUE,
      value: ALL_COUNTRY_VALUE,
      label: (
        <>
          All <GlobalOutlined />
        </>
      ),
    });
  }

  countryList.forEach((country) => {
    if (isCountryAllowed(allowedCountries, country)) {
      options.push({
        key: country + (showCountryCallingCode ? `+${getCountryCallingCode(country)}` : ''),
        value: country,
        label: (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              {getUnicodeFlagIcon(country)}
              {' ' + country}
            </div>
            <div>{showCountryCallingCode && `+${getCountryCallingCode(country)}`}</div>
          </div>
        ),
      });
    }
  });

  return (
    <Select
      {...props}
      showSearch={true}
      optionFilterProp="children"
      dropdownMatchSelectWidth={false}
      filterOption={(input: string, option: any) => option?.key?.toLowerCase()?.includes(input?.toLowerCase())}
      options={options}
    />
  );
};
